import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { BRAND } from "@/constants/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, name);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-stretch">
      <div className="relative hidden lg:flex flex-col justify-between w-[45%] border-r border-border p-14 overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${BRAND.photos.tribal})`,
            backgroundSize: "420px",
            opacity: 0.35,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/85 to-[#0A0A0A]/60" />
        <img
          src={BRAND.logo}
          alt="Smash Lemonade"
          className="relative w-56 drop-shadow-[0_6px_24px_rgba(0,0,0,0.9)]"
        />
        <div className="relative">
          <h1 className="neon text-6xl leading-[0.95] xl:text-7xl">
            Welcome to the
            <br />
            <span className="text-primary">smash life</span>
          </h1>
          <p className="brand-script mt-4 text-3xl text-primary">{BRAND.mantra}</p>
          <p className="mt-5 max-w-md text-sm text-muted-foreground">{BRAND.quote}</p>
        </div>
        <div className="relative flex items-center gap-5 mono text-[11px] text-muted-foreground">
          <Link
            to="/start"
            data-testid="login-pricing-link"
            className="tracking-widest text-primary hover:underline"
          >
            PRICING →
          </Link>
          <Link
            to="/founders"
            data-testid="login-founders-link"
            className="tracking-widest text-primary hover:underline"
          >
            FOUNDERS →
          </Link>
          <span>Progress is saved to your account, not this device.</span>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm fade-up" data-testid="auth-form">
          <h2 className="display neon text-4xl">
            {mode === "login" ? "Operator login" : "Create operator"}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {mode === "login" ? "Pick up where you left off." : "Start the roadmap from milestone one."}
          </p>

          <div className="mt-8 space-y-4">
            {mode === "register" && (
              <div>
                <Label className="mono text-[11px] tracking-widest text-muted-foreground">NAME</Label>
                <Input
                  data-testid="name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1 rounded-sm bg-[#0A0A0A]"
                  placeholder="Jordan"
                />
              </div>
            )}
            <div>
              <Label className="mono text-[11px] tracking-widest text-muted-foreground">EMAIL</Label>
              <Input
                data-testid="email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 rounded-sm bg-[#0A0A0A]"
                placeholder="you@truck.com"
              />
            </div>
            <div>
              <Label className="mono text-[11px] tracking-widest text-muted-foreground">PASSWORD</Label>
              <Input
                data-testid="password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 rounded-sm bg-[#0A0A0A]"
                placeholder="••••••••"
              />
            </div>
          </div>

          {error && (
            <div
              data-testid="auth-error"
              className="mt-4 border border-destructive/60 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {error}
            </div>
          )}

          <Button
            data-testid="auth-submit"
            type="submit"
            disabled={busy}
            className="mt-6 w-full rounded-sm bg-primary text-primary-foreground display text-xl uppercase tracking-wide hover:bg-[#E6C800] transition-colors"
          >
            {busy ? "Working..." : mode === "login" ? "Enter the academy" : "Create account"}
          </Button>

          <button
            type="button"
            data-testid="auth-toggle"
            onClick={() => {
              setMode(mode === "login" ? "register" : "login");
              setError("");
            }}
            className="mt-4 w-full text-xs text-muted-foreground hover:text-primary transition-colors"
          >
            {mode === "login" ? "No account yet? Create one" : "Already an operator? Log in"}
          </button>

          <Link
            to="/start"
            data-testid="login-pricing-link-mobile"
            className="mono mt-6 block text-center text-[11px] tracking-widest text-primary hover:underline lg:hidden"
          >
            SEE PRICING & FREE GUIDE →
          </Link>
          <Link
            to="/founders"
            data-testid="login-founders-link-mobile"
            className="mono mt-3 block text-center text-[11px] tracking-widest text-primary hover:underline lg:hidden"
          >
            MEET THE FOUNDERS →
          </Link>
        </form>
      </div>
    </div>
  );
}
